/**
 * Keyboard Shortcuts Hook
 *
 * Provides global keyboard shortcuts for the web app.
 * - N: Open new entry modal
 * - /: Focus search input (on Timeline page)
 * - Esc: Close any open modal
 */

import { useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface KeyboardShortcutsOptions {
  onNewEntry?: () => void;
  onSearch?: () => void;
  onEscape?: () => void;
}

const SEARCH_INPUT_SELECTOR = 'input[placeholder*="Search"]';

/**
 * Check if user is typing in an input/textarea
 */
function isTyping(event: KeyboardEvent): boolean {
  const target = event.target as HTMLElement;
  const tagName = target.tagName.toLowerCase();
  const isEditable = target.isContentEditable;
  const isInput = tagName === 'input' || tagName === 'textarea' || tagName === 'select';

  return isEditable || isInput;
}

/**
 * Hook for handling global keyboard shortcuts
 */
export function useKeyboardShortcuts(options: KeyboardShortcutsOptions = {}) {
  const location = useLocation();
  const navigate = useNavigate();

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in inputs (except Escape)
      if (event.key !== 'Escape' && isTyping(event)) {
        return;
      }

      // Don't trigger if modifier keys are pressed (except Escape)
      if (event.key !== 'Escape' && (event.metaKey || event.ctrlKey || event.altKey)) {
        return;
      }

      switch (event.key.toLowerCase()) {
        case 'n': {
          // Open new entry modal
          options.onNewEntry?.();
          event.preventDefault();
          break;
        }

        case '/': {
          // Focus search input
          // If not on Timeline page, navigate there first
          if (location.pathname !== '/timeline') {
            navigate('/timeline');
            // Use a short delay to wait for navigation
            setTimeout(() => {
              const searchInput = document.querySelector<HTMLInputElement>(SEARCH_INPUT_SELECTOR);
              searchInput?.focus();
            }, 100);
          } else {
            const searchInput = document.querySelector<HTMLInputElement>(SEARCH_INPUT_SELECTOR);
            searchInput?.focus();
          }
          options.onSearch?.();
          event.preventDefault();
          break;
        }

        case 'escape': {
          // Trigger escape callback (for closing modals)
          options.onEscape?.();
          // Also blur any focused element
          if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
          }
          break;
        }
      }
    },
    [location.pathname, navigate, options]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
}

/**
 * Component that displays keyboard shortcuts help
 */
export function KeyboardShortcutsHelp() {
  return (
    <div className="text-xs text-text-tertiary space-y-1">
      <p>
        <kbd className="px-1.5 py-0.5 rounded bg-bg-tertiary font-mono">N</kbd>
        <span className="ml-2">New entry</span>
      </p>
      <p>
        <kbd className="px-1.5 py-0.5 rounded bg-bg-tertiary font-mono">/</kbd>
        <span className="ml-2">Search</span>
      </p>
      <p>
        <kbd className="px-1.5 py-0.5 rounded bg-bg-tertiary font-mono">Esc</kbd>
        <span className="ml-2">Close / Blur</span>
      </p>
    </div>
  );
}
